// FORM DISPLAY MODE DECISION:
//   Overlay desktop (two-pane) → 'overlay' → fixed height (STANDARDIZED_FORM_HEIGHT)
//   Everything else            → 'dynamic' → null (content-driven)
//
// Computed by layout containers, NOT by form components:
//   OverlayElement.jsx: desktop='overlay', mobile='dynamic'
//   App.jsx (embed):    always 'dynamic'
//   HostedNormalLayout:  always 'dynamic'

// ─── Centralized Height System ───────────────────────────────────────
// All container-width-specific heights live in forms.css via container queries.
// JS constants here are DEFAULTS / FALLBACKS. The CSS classes override them
// at specific container widths via @container embed-form queries.
//
// Height tiers (defined once in CSS, referenced by class name):
// Detail and form pages share the same height at each tier for seamless transitions.
//
//   Container Width   │ Detail Page (fixed)   │ Form skeleton (min-height)
//   ──────────────────┼───────────────────────┼────────────────────────
//   < 320px           │ 460px                 │ 460px
//   320px – 448px     │ 520px                 │ 520px
//   449px+            │ 560px                 │ 560px
//
// CSS classes:
//   .dynamic-detail-height  → applied to detail page frosted container
//   .dynamic-form-min-height → applied to the form SKELETON only. Real form
//                              cards hug their content (CON-529 F9, Paper);
//                              the container's min-height transition eases
//                              the skeleton → form swap down to it.
//
// To change heights: edit the values in forms.css container query blocks.
// ─────────────────────────────────────────────────────────────────────

export const STANDARDIZED_FORM_HEIGHT_PX = 560;
export const STANDARDIZED_FORM_HEIGHT = `${STANDARDIZED_FORM_HEIGHT_PX}px`;

// Default minimum height for dynamic-mode forms.
// CSS container queries (.dynamic-form-min-height) override this per width.
export const DYNAMIC_FORM_MIN_HEIGHT_PX = 560;
export const DYNAMIC_FORM_MIN_HEIGHT = `${DYNAMIC_FORM_MIN_HEIGHT_PX}px`;

// Drawer mode minimum height — used by FormStartPageShell and FormSkeleton
// to prevent the mobile bottom-sheet from collapsing during loading or
// when form content is minimal. Not responsive (drawer is always full-width).
export const DRAWER_FORM_MIN_HEIGHT_PX = 400;
export const DRAWER_FORM_MIN_HEIGHT = `${DRAWER_FORM_MIN_HEIGHT_PX}px`;

// Overlay card minimum height — applied to the frosted-background wrapper
// in OverlayElement desktop layout. Prevents the two-pane card from shrinking
// when form pages transition between different content lengths.
// Clamped to viewport via CSS min() so it degrades on short screens.
export const OVERLAY_CARD_MIN_HEIGHT_PX = 880;
export const OVERLAY_CARD_MIN_HEIGHT = `min(${OVERLAY_CARD_MIN_HEIGHT_PX}px, calc(100vh - 4rem))`;

// Standard forms are shorter than product/crowdfunding forms in overlay
export const OVERLAY_CARD_STANDARD_MIN_HEIGHT_PX = 700;
export const OVERLAY_CARD_STANDARD_MIN_HEIGHT = `min(${OVERLAY_CARD_STANDARD_MIN_HEIGHT_PX}px, calc(100vh - 4rem))`;

export function getStandardizedFormHeight({ formDisplayMode = 'dynamic' } = {}) {
  return formDisplayMode === 'overlay' ? STANDARDIZED_FORM_HEIGHT : null;
}

// CON-529 F9 (Paper: "cards hug their content"). In dynamic mode a REAL form
// card carries no height floor — the 560 floor left 50–170px of empty card above
// the CTA on short steps. Only loading frames keep it (`floor: true`): the
// skeletons, so the card does not collapse while the form arrives (CON-160).
export function getStandardizedFormElementStyles({ formDisplayMode = 'dynamic', floor = false } = {}) {
  const standardizedHeight = getStandardizedFormHeight({ formDisplayMode });

  if (!standardizedHeight) {
    return floor
      ? { minHeight: DYNAMIC_FORM_MIN_HEIGHT, display: 'flex', flexDirection: 'column' }
      : { display: 'flex', flexDirection: 'column' };
  }

  return {
    height: standardizedHeight,
    minHeight: standardizedHeight,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
}

export function getStandardizedFormBodyStyles({ formDisplayMode = 'dynamic' } = {}) {
  const standardizedHeight = getStandardizedFormHeight({ formDisplayMode });

  if (!standardizedHeight) {
    return {
      flex: '1 1 auto',
      minHeight: 0,
    };
  }

  return {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  };
}

/**
 * The form card's classes, one recipe for every step's card (FormStartPageShell, FormPageShell,
 * the ticket form): the frosted card in the embed and the hosted page, nothing extra in the
 * overlay pane (fixed height) or the mobile drawer (its own floor). In the embed and the hosted
 * page the card also takes the page floor (owner call 2026-10-23): at least as tall as the detail
 * page at that width — the event tiers for event forms — with the CTA (FormActionButtons'
 * mt-auto) at the bottom of a short step.
 */
export function formCardClass({ isDrawerOpen = false, isHostedPage = false, isOverlay = false, isEvent = false } = {}) {
  if (isOverlay) return 'flex-1 flex flex-col';
  if (isDrawerOpen) return '';
  const floor = isEvent ? 'event-form-min-height' : 'dynamic-form-min-height';
  return `frosted-background cs-form-card ${isHostedPage ? '' : 'dynamic-frosted-margin'} ${floor}`.replace(/\s+/g, ' ').trim();
}

